import { useAppSelector } from 'hooks/redux';
import type { OrderedMap } from 'immutable';
import React, { memo, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoIcon from '@mui/icons-material/Info';
import { Button, Chip, Divider, Tooltip, Typography } from '@mui/material';

import { businessSelectors } from 'app-components/flux/business';
import { chartOfAccountsSelectors, chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import type {
  ChartOfAccountNodeProps,
  ChartOfAccountProps,
} from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { customersSelectors } from 'app-components/flux/customers';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { projectsSelectors } from 'app-components/flux/projects';
import type { Tag } from 'app-components/flux/tags/tagsTypes';
import type { SplitItemProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';

import QAmountField from 'components/QAmountField';
import UsageAndEntityIcon from 'components/UsageAndEntityIcon';

import { lookupTagName } from 'data/transactions/utils';
import { OtherSideField } from '../Transactions/DetailsDialog/fields';
import { isOverflowSplitItem } from 'app-components/flux/transactions/splitsHelpers';
import { useSplitsViewStyles } from './splitsViewStyles';

const getCoaNode = (
  coaId: string,
  coaNodesById: OrderedMap<string, ChartOfAccountNodeProps>,
  value?: ChartOfAccountProps | null,
) => {
  let ret = coaNodesById ? coaNodesById.get(coaId) : null;

  if (chartOfAccountsUtils.isTransferCoaNotFound(value)) {
    ret = coaNodesById.get(chartOfAccountsUtils.TRANSFER_NOT_FOUND_COA_ID);
  }
  return ret;
};

type Props = {
  items: SplitItemProps[];
  txnId?: string | null;
  goToOtherSide?: (txn: TransactionProps) => void;
  variant: 'default' | 'series';
};

const SplitItemsList = ({ items, txnId, goToOtherSide, variant }: Props): JSX.Element => {
  const { classes, cx } = useSplitsViewStyles();
  const isSeriesVariant = variant === 'series';
  const [showAll, setShowAll] = useState(false);

  const coaIDs = useAppSelector((state) => chartOfAccountsSelectors.getChartOfAccountNodesById(state));
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const projects = useAppSelector(projectsSelectors.getProjects);
  const customers = useAppSelector(customersSelectors.getCustomers);
  const businesses = useAppSelector(businessSelectors.getResourcesById);

  return (
    <div key='splitView' className={cx(classes.splitPadding, isSeriesVariant && classes.splitPaddingSeries)}>
      {items.map((splitItem, index) => {
        if (index >= 4 && !showAll) return null;

        const coaId = chartOfAccountsUtils.getCoaId(splitItem.coa);
        const catName = getCoaNode(coaId, coaIDs, splitItem.coa)?.fullPathNameWithoutUsageType;
        const business = businesses.get(splitItem.businessId ?? '');
        const isOverflow = isOverflowSplitItem(splitItem);
        const supportedTransfer =
          txnId &&
          splitItem?.coa &&
          splitItem.coa.type === 'ACCOUNT' &&
          chartOfAccountsUtils.isSupportedTransferCoa(splitItem.coa);

        return (
          <div key={splitItem.id || index} id={`${index}-split-row`}>
            <div role='presentation' className={cx(classes.rowRoot, isSeriesVariant && classes.rowRootSeries)}>
              {isSeriesVariant ? (
                <div className={classes.seriesLeftGrid}>
                  <div className={classes.seriesCell}>
                    <div className={classes.catRow}>
                      <Typography className={classes.categoryText}>{catName}</Typography>
                    </div>
                    {supportedTransfer && goToOtherSide ? (
                      <OtherSideField
                        coa={splitItem.coa}
                        txnId={txnId}
                        transferId={splitItem?.transfer?.id}
                        altAmount={splitItem.amount ?? 0}
                        goToOtherSide={goToOtherSide}
                        isSplit
                      />
                    ) : null}
                    <div className={classes.splitTags}>
                      {splitItem?.tags?.map((tag: Tag) => {
                        const tagName = lookupTagName(tag);
                        return <Chip key={tagName} label={tagName} className={classes.chip} />;
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className={classes.catRow}>
                    {businessEnabled && (
                      <div className={classes.padding}>
                        <UsageAndEntityIcon
                          color={business?.color ?? ''}
                          size='xsmall'
                          usageIcon={splitItem.businessId ? (business?.taxGroup ?? 'BUSINESS') : 'PERSONAL'}
                        />
                      </div>
                    )}
                    <Typography>{catName}</Typography>
                  </div>
                  {supportedTransfer && goToOtherSide ? (
                    <OtherSideField
                      coa={splitItem.coa}
                      txnId={txnId}
                      transferId={splitItem?.transfer?.id}
                      altAmount={splitItem.amount ?? 0}
                      goToOtherSide={goToOtherSide}
                      isSplit
                    />
                  ) : null}
                  <div className={classes.splitTags}>
                    {splitItem?.tags?.map((tag: Tag) => {
                      const tagName = lookupTagName(tag);
                      return <Chip key={tagName} label={tagName} className={classes.chip} />;
                    })}
                  </div>
                </>
              )}

              <div
                id={`${index}-amount`}
                className={cx(classes.amountWidth, isSeriesVariant && classes.amountWidthSeries)}
              >
                {isOverflow && splitItem.amount === 0 ? (
                  <div className={cx(classes.overflowAmount, isSeriesVariant && classes.overflowAmountSeries)}>
                    <Tooltip title='Any extra amount is assigned to this Split.'>
                      <InfoIcon className={classes.infoIcon} />
                    </Tooltip>
                    <Typography variant='bodyRegular'>Overflow</Typography>
                  </div>
                ) : (
                  <QAmountField
                    margin='normal'
                    classes={{ input: classes.inputField }}
                    textFieldVariant='filled'
                    editable={false}
                    plain
                    value={splitItem.amount ? splitItem.amount : 0}
                    id={`split-${index}-amount`}
                    showSign='positive'
                    showColor='positive'
                  />
                )}
                {businessEnabled && splitItem.customerId && customers?.get(splitItem?.customerId) && (
                  <Typography variant='bodyMedium'>
                    {customers?.get(splitItem?.customerId)?.businessName ||
                      `${customers.get(splitItem.customerId)?.firstName} ${customers.get(splitItem.customerId)?.lastName}`}
                  </Typography>
                )}
                {businessEnabled && splitItem.projectId && projects.get(splitItem.projectId) && (
                  <Typography variant='bodyRegular'>{projects.get(splitItem.projectId)?.name ?? ''}</Typography>
                )}
              </div>
            </div>

            {(index !== items.length - 1 || showAll) && <Divider />}
          </div>
        );
      })}

      {items.length > 4 && (
        <Button
          id='show-all-split-row'
          className={classes.showAllButton}
          onClick={() => {
            tracker.track(tracker.events.splitsV2ShowMore, { willShow: showAll ? 'less' : 'more' });
            setShowAll(!showAll);
          }}
        >
          Show {showAll ? 'Less' : 'All'}
          {showAll ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </Button>
      )}
    </div>
  );
};

export default memo(SplitItemsList);
